import { memo, Suspense, useEffect, useState, useTransition } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

/**Import Assets */
import styles from "./styles/ProtectedLayout.module.scss";
// import cssStyles from './styles/notification.module.css';

/**Import Custom Component */
import { WEB_ROUTES } from "../../constants";
import Navbar from "./Navbar";

// Icons
import { AiOutlineUsergroupAdd } from "react-icons/ai";
import { BiPhotoAlbum, BiSolidBookContent } from "react-icons/bi";
import { CgSearchFound } from "react-icons/cg";
import { FaInfinity, FaUser } from "react-icons/fa";
import { IoNotifications } from "react-icons/io5";
import { TbReportAnalytics } from "react-icons/tb";
import {
  MdAccessTimeFilled,
  MdDashboard,
  MdPolicy
} from "react-icons/md";
import { RiAdvertisementFill } from "react-icons/ri";
import { useLogoutUserMutation } from "../../Data/services/userAuthApi";
import Toast from "../../Hooks/useToast";
import LogoutModal from "../Modals/LogoutModal";
import TransitionLink from "../Routes/TransitionLink";
import TopProgressBar from "../TopProgressBar/TopProgressBar.js";
const ProtectedLayout = ({ isSearchBar, isSettingButton, children }) => {
  const [collapsed, setCollapsed] = useState(window.innerWidth > 991);
  const location = useLocation();
  const [logoutModal, setLogoutModal] = useState(false);
  const [isPending, startTransition] = useTransition();
  const { logout } = useAuth();

  const isActiveRoute = (route) => {
    const regex = new RegExp(`^${route}`);
    return regex.test(location.pathname);
  };

  const navigate = useNavigate();

  const items = [
    {
      label: "Dashboard",
      icon: <MdDashboard />,
      route: WEB_ROUTES.DASHBOARD,
    },
    {
      label: "User Management",
      icon: <FaUser />,
      route: WEB_ROUTES.USER_MANAGEMENT,
    },
    // {
    //   label: "Categories Management",
    //   icon: <MdOutlineMiscellaneousServices />,
    //   route: WEB_ROUTES.CATEGORY_MANAGEMENT,
    // },
    {
      label: "Referrals",
      icon: <AiOutlineUsergroupAdd />,
      route: WEB_ROUTES.REFERRALS_MANAGEMENT,
    },
    {
      label: "Forever Free",
      icon: <FaInfinity />,
      route: WEB_ROUTES.FOREVER_FREE_REFERRALS,
    },
    {
      label: "Albums",
      icon: <BiPhotoAlbum />,
      route: WEB_ROUTES.ALBUMS_MANAGEMENT,
    },
    {
      label: "Contents",
      icon: <BiSolidBookContent />,
      route: WEB_ROUTES.CONTENTS_MANAGEMENT,
    },
    {
      label: "Reports",
      icon: <TbReportAnalytics />,
      route: WEB_ROUTES.REPORTS_MANAGEMENT,
    },
    {
      label: "Founding Members",
      icon: <CgSearchFound />,
      route: WEB_ROUTES.FOUNDING_MEMBER_MANAGEMENT,
    },
    {
      label: "Early Access Incentives",
      icon: <MdAccessTimeFilled />,
      route: WEB_ROUTES.EARLY_ACCESS_MANAGEMENT,
    },
    {
      label: "Advertisement Type",
      icon: <RiAdvertisementFill />,
      route: WEB_ROUTES.ADVERTISEMENT_MANAGEMENT,
    },
    {
      label: "Send Notification",
      icon: <IoNotifications />,
      route: WEB_ROUTES.SEND_NOTIFICATION,
    },
    {
      label: "Content Management",
      icon: <MdPolicy />,
      route: WEB_ROUTES.PAGE_MANAGEMENT,
    },
  ];

  const handleSettings = () => {
    startTransition(() => {
      navigate(WEB_ROUTES.SETTING);
    });
  };

  const handleToggle = () => {
    setCollapsed(!collapsed);
  };

  const handleMenuClose = () => {
    setCollapsed(false);
  };

  const handleLogoutModalOpen = () => {
    setLogoutModal(true);
  };

  const handleLogoutModalClose = () => {
    setLogoutModal(false);
  };

  const [logoutUser, { isLoading }] = useLogoutUserMutation();

  const handleLogout = async () => {
    try {
      const response = await logoutUser().unwrap();
      if (response.code === 200) {
        logout();
        startTransition(() => {
          navigate("/account");
        });
        Toast("Logged Out Successfully", "success");
      }
    } catch {
      Toast("An error occurred during log out", "error");
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 991) {
        setCollapsed(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className={styles.ProtectedLayout}>
      <div
        className={collapsed ? styles.background : ""}
        onClick={handleMenuClose}
      ></div>
      <div
        className={`${styles.menu} ${collapsed ? `${styles.collapsed} visible` : styles.notCollapsed
          }`}
        id="sider"
      >
        <TransitionLink
          to={`${process.env.REACT_APP_DOMAIN_DIR}/dashboard`}
          className={`${styles.logo} ${!collapsed ? styles.notCollapsed : ""} `}
        >
          <img
            src={collapsed ? "/images/logo_name.png" : "/images/small_logo.png"} //todo:
            alt="Logo"
            className={`${styles.imageNotCollapsed} image-fluid object-fit-contain`}
            width={"100%"}
            height={"100%"}
          />
        </TransitionLink>
        {items.map(({ icon, label, route, lineBreak }) => (
          <div key={route}>
            <TransitionLink
              to={route}
              className={`${styles.item} ${isActiveRoute(route) ? styles.active : ""
                } ${!collapsed ? styles.collapsed : ""}`}
            >
              <span style={{ fontSize: "20px", display: "flex" }}>{icon}</span>
              {collapsed ? <p>{label}</p> : ""}
            </TransitionLink>
            {lineBreak && <hr className="w-100 m-0"></hr>}
          </div>
        ))}
      </div>
      <div className={styles.routeContent}>
        {logoutModal && (
          <LogoutModal
            handleLogoutModalClose={handleLogoutModalClose}
            data={null}
            loading={isLoading}
            onLogout={handleLogout}
          />
        )}
        <Navbar
          isSearchBar={isSearchBar}
          isSettingButton={isSettingButton}
          handleToggle={handleToggle}
          handleLogoutModalOpen={handleLogoutModalOpen}
          handleSettings={handleSettings}
        />
        <div className={styles.content}>
          <Suspense fallback={<TopProgressBar />}>{children}</Suspense>
        </div>
      </div>
    </div>
  );
};

export default memo(ProtectedLayout);
